'use client'

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { cn } from '@/lib/utils'
import { useSearchStore, useSettingsStore, useUIStore } from '@/lib/store'
import { gridColumnsForCardSize } from '@/lib/constants'
import { medianOf } from '@/components/roostr'
import { useGridKeyboardNav } from '@/lib/use-grid-keyboard-nav'
import { ListingCard, effectiveListingPrice, type ListingCardStats } from './listing-card'
import { ListingCardSkeleton } from './listing-card-skeleton'
import { KeyboardHintBar, ShortcutsSheet } from './keyboard-hints'

interface ListingsGridProps {
  className?: string
}

export function ListingsGrid({ className }: ListingsGridProps) {
  const { results, isLoading, hiddenListings, showAllHidden, filters } = useSearchStore()
  const { cardSize } = useSettingsStore()
  const isCompact = cardSize < 280
  const gridGap = isCompact ? 'gap-3' : 'gap-6'
  // The density slider picks an explicit column count (even travel per step);
  // 1 column is a single full-width card. The slider is hidden below sm
  // (search toolbar), so narrow viewports CLAMP instead of honoring the
  // persisted desktop choice: <640px always 1 col, 640-1023px at most 2 —
  // otherwise a phone renders the stored 3-col layout with unreadable cards
  // and no visible control to fix it. Tailwind's grid-cols-n compiles to
  // repeat(n, minmax(0,1fr)) — identical to the old inline style at lg+.
  const columns = gridColumnsForCardSize(cardSize)
  const gridColsClass =
    {
      1: 'grid-cols-1',
      2: 'grid-cols-1 sm:grid-cols-2',
      3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
      4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
      5: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5',
    }[columns] ?? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'

  // Single memoized visible pool — both the render list and the stats derive
  // from it (no double filtering).
  const visibleResults = React.useMemo(
    () => results.filter((r) => !hiddenListings.has(r.id)),
    [results, hiddenListings]
  )
  const hiddenCount = results.length - visibleResults.length
  const useTotalPrice = filters.use_total_price

  // Page stats pool from the currently visible (non-hidden) results — feeds
  // each card's StatRow vizzes and PricePositionBar. Prices use the same
  // effective-price operand as the number each card displays (base rent +
  // recurring extras when use_total_price is on), and the same index-median
  // as the search page's insights strip. With <2 usable values the primitives
  // render nothing (they guard internally).
  const stats = React.useMemo<ListingCardStats>(() => {
    const prices = visibleResults
      .map((r) => effectiveListingPrice(r, useTotalPrice))
      .filter((n) => Number.isFinite(n) && n > 0)
    const areas = visibleResults
      .map((r) => r.area)
      .filter((n): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0)
    const floors = visibleResults
      .map((r) => r.floor)
      .filter((n): n is number => typeof n === 'number' && Number.isFinite(n))
    const hasPrices = prices.length >= 2
    return {
      prices,
      areas,
      floors,
      priceMin: hasPrices ? Math.min(...prices) : 0,
      priceMax: hasPrices ? Math.max(...prices) : 0,
      priceMedian: hasPrices ? medianOf(prices) : 0,
    }
  }, [visibleResults, useTotalPrice])

  // Progressive rendering: start with 0 cards and gradually increase
  const [visibleCount, setVisibleCount] = React.useState(0)
  const BATCH_SIZE = 6 // Render 6 cards at a time
  const BATCH_DELAY = 50 // 50ms between batches

  // Reset visible count when results change or component remounts
  React.useEffect(() => {
    setVisibleCount(0)

    // Progressively render cards in batches
    let currentBatch = 0

    const renderNextBatch = () => {
      currentBatch++
      const newCount = Math.min(currentBatch * BATCH_SIZE, results.length)
      setVisibleCount(newCount)

      if (newCount < results.length) {
        timeoutId = setTimeout(renderNextBatch, BATCH_DELAY)
      }
    }

    // Start rendering after a brief delay to let skeletons show
    let timeoutId = setTimeout(renderNextBatch, 100)

    return () => {
      clearTimeout(timeoutId)
    }
    // Keyed on `results` (the search result set) and NOT on visibleResults.length:
    // hiding one card changes that length, which reset visibleCount to 0 and
    // rebuilt all 20 cards batch by batch — a visible flash, and it dropped
    // keyboard browsing on the X key. Hiding now just removes that one card.
  }, [results])

  // Keyboard browsing. Blocked while the palette or the fullscreen gallery owns
  // the keyboard — both are plain `fixed inset-0` layers with no dialog role, so
  // the store flags are the reliable signal.
  const gridRef = React.useRef<HTMLDivElement>(null)
  const router = useRouter()
  const { commandPaletteOpen, galleryOpen } = useUIStore()
  const openListing = React.useCallback(
    (listingId: string, newTab: boolean) => {
      // Keyboard flow stays in this tab (a new tab steals focus and ends the
      // run); ⌘/Ctrl+Enter is the explicit background-tab escape hatch. The
      // card's own mouse click keeps opening a new tab, unchanged.
      if (newTab) window.open(`/listings/${listingId}`, '_blank', 'noopener,noreferrer')
      else router.push(`/listings/${listingId}`)
    },
    [router]
  )
  const kbd = useGridKeyboardNav({
    containerRef: gridRef,
    count: Math.min(visibleResults.length, visibleCount),
    blocked: commandPaletteOpen || galleryOpen,
    onOpen: openListing,
  })

  // "Browse results with your keyboard" in the command palette — the palette is
  // global while the grid is /search-only, so it reaches us the same loose way
  // the saved-searches panel is opened.
  React.useEffect(() => {
    const open = () => kbd.setShortcutsOpen(true)
    window.addEventListener('open-keyboard-shortcuts', open)
    return () => window.removeEventListener('open-keyboard-shortcuts', open)
  }, [kbd])

  // Show skeletons during initial loading
  if (isLoading) {
    const skeletonCount = Math.min(visibleResults.length || 9, 12)
    return (
      <div className={cn('grid', gridColsClass, gridGap, className)}>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <ListingCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  if (results.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-lg text-muted-foreground">
          No listings found. Try adjusting your filters.
        </p>
      </div>
    )
  }

  // Split results into what's rendered vs what's still skeleton
  const cardsToRender = visibleResults.slice(0, visibleCount)
  const skeletonsToShow = visibleResults.length - visibleCount

  return (
    <div className={className}>
      {/* Hidden listings notice */}
      {hiddenCount > 0 && (
        <div className="mb-4 flex items-center justify-between px-2 py-2 bg-muted rounded-lg">
          <span className="text-sm text-muted-foreground">
            {hiddenCount} listing{hiddenCount !== 1 ? 's' : ''} hidden
          </span>
          <button
            onClick={showAllHidden}
            className="text-sm font-medium text-primary hover:underline"
          >
            Show all
          </button>
        </div>
      )}

      {/* Grid - items-stretch ensures all cards in a row have same height */}
      <div ref={gridRef} className={cn('grid items-stretch', gridColsClass, gridGap)}>
        {/* Render actual cards progressively */}
        {cardsToRender.map((listing, i) => (
          <ListingCard
            key={listing.id}
            listing={listing}
            stats={stats}
            className="h-full"
            tabIndex={kbd.tabIndexFor(i)}
            focused={kbd.index === i}
          />
        ))}

        {/* Show skeletons for cards not yet rendered */}
        {skeletonsToShow > 0 && Array.from({ length: skeletonsToShow }).map((_, i) => (
          <ListingCardSkeleton key={`skeleton-${i}`} />
        ))}
      </div>

      <KeyboardHintBar visible={kbd.hintVisible} onOpenShortcuts={() => kbd.setShortcutsOpen(true)} />
      <ShortcutsSheet open={kbd.shortcutsOpen} onClose={() => kbd.setShortcutsOpen(false)} />
    </div>
  )
}

